<template>
        <div class="mx-auto">
            <div class="loader" v-if="loading"></div>
            <div v-else>
                <page-title :title="$t('externalSupplier.title')" :showTitle="false"></page-title>

                <div class="btn-group" role="group" aria-label="Basic example">
                    <button type="button" class="btn btn-primary" @click="backToRegister">
                        <i class="fas fa-th-list"></i>
                        {{ $t('backToList') }}
                    </button>
                </div>

                <div class="card mt-3">
                    <div class="card-header bg-primary text-white">
                        <div class="card-title h5 m-0">{{ $t('externalSupplier.contractData') }}</div>
                    </div>
                    <div class="card-body">
                        <div v-for="(contract,index) in externalSupplier.contracts"
                            :key="index"
                            class="mt-3 border border-light-2 p-2 ">
                            <contract :title="contract.name" :contract="contract" :showButton="false"></contract>
                            <br>
                            <div v-if="contract.tariffs && Array.isArray(contract.tariffs) && contract.tariffs.length>0" class="form-group">
                                <div v-for="tariff in contract.tariffs" :key="tariff.id">
                                    <TariffEntityDetails :id="tariff.id" :showContractInfo="false" :allowEdit="false"></TariffEntityDetails>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card mt-3">
                    <div class="card-header bg-primary text-white">
                        <div class="card-title h5 m-0">{{ $t('externalSupplier.company') }}</div>
                    </div>
                    <div class="card-body row">
                        <div class="form-group col-4 ">
                            <label :for="'number' + _uid" class="font-weight-bold">{{ $t('externalSupplier.id') }}</label>
                            <input type="number"
                                   class="form-control"
                                   :id="'id' + _uid"
                                   :name="'id' + _uid"
                                   v-model="externalSupplier.id"
                                   :data-vv-as="$t('externalSupplier.id')"
                                   readonly />
                        </div>
                        <div class="form-group col-4">
                            <label :for="'number' + _uid" class="font-weight-bold">
                                {{ $t('externalSupplier.status') }}
                            </label>
                            <toggle :value="externalSupplier.deactivated" :disabled="true"></toggle>
                        </div>
                        <div class="form-group col-4">
                            <label :for="'number' + _uid" class="font-weight-bold">
                                {{ $t('externalSupplier.isCompany') }}
                            </label>
                            <toggle :value="externalSupplier.isCompany" :disabled="true" v-model="externalSupplier.isCompany"></toggle>
                        </div>
                    </div>
                    <div class="card-body" v-if="externalSupplier.company">
                        <company :company="externalSupplier.company"
                            :showCompanyContact="false"
                            :showCompanyManagerButton="false"></company>
                    </div>
                    <div class="card-body" v-if="externalSupplier.person">
                        <person :person="externalSupplier.person"
                                :requiredIdentification="false"
                                :showExtended="false"></person>
                    </div>
                </div>
                <div class="card mt-3">
                    <div class="card-header bg-primary text-white">
                        <div class="card-title h5 m-0">{{ $t('externalSupplier.contactPersons') }}</div>
                    </div>
                    <div class="card-body">
                        <div v-for="(contactPerson,index) in externalSupplier.contacts" :key="index">
                            <hr />
                            <contactPerson :person="contactPerson"></contactPerson>
                        </div>
                    </div>
                </div>
                <div class="card mt-3">
                    <div class="card-header bg-primary text-white">
                        <div class="card-title h5 m-0">{{ $t('otherFields.otherFields') }}</div>
                    </div>
                    <div class="card-body">
                        <otherFields :otherFields="externalSupplier.otherFields"></otherFields>
                    </div>
                </div>
                <div class="card mt-3">
                    <div class="card-header bg-primary text-white">
                        <div class="card-title h5 m-0">{{ $t('registers.lectors.attachedDocuments') }}</div>
                    </div>
                    <div class="card-body">
                        <div v-for="(document,index) in externalSupplier.documents" :key="index" class="mt-2 border border-light-2 p-2 k-display-flex">
                            <file-download :file="document"
                                           class="col-12"
                                           :type="document.docTypeCode">
                            </file-download>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-3" v-if="externalSupplier">
                <AuxiliaryRegisterDetails :entryId="externalSupplier.auxRegisterEntityId"></AuxiliaryRegisterDetails>
            </div>

            <div v-if="id">
                <HistoryRecordsGrid :id="id"
                                    :url="'api/history/externalSupplier'"
                                    :columns="externalSupplierHistoryGridColumns"
                                    :schemaModelFields="externalSupplierHistoryGridSchemaModelFields"
                                    :componentName="'ExternalSupplierHistory'">
                </HistoryRecordsGrid>
            </div>
        </div>

</template>

<script>
    import OtherFields from "@/components/otherFields/OtherFieldsDetails.vue";
    import PageTitle from "@/components/common/PageTitle";
    import Company from "@/components/company/CompanyDetails";
    import PersonDetailsComponent from "@/components/person/PersonDetails";
    import ContractComponent from "@/components/contract/ContractDetails"
    import ContactPerson from "@/components/person/ContactPersonDetails";
    import Toggle from "@/components/toggle/Toggle";
    import { ExternalSupplierDetails } from "@/models/models";
    import FileDownload from '@/components/common/FileDownload';
    import TariffEntityDetails from '@/components/finance/TariffEntityDetails';
    import HistoryRecordsGrid from '@/components/history/HistoryRecordsGrid';
    import { externalSupplierHistoryGridColumns, externalSupplierHistoryGridSchemaModelFields } from '@/settings/historyGridsSettings';
    import AuxiliaryRegisterDetails from '@/components/dynamic/AuxiliaryRegisterDetails'

    export default {
        name: 'ExternalSupplierDetails',
        components: {
            PageTitle,
            OtherFields,
            Company,
            ContactPerson,
            Toggle,
            'contract': ContractComponent,
            'file-download': FileDownload,
            'person': PersonDetailsComponent,
            TariffEntityDetails,
            HistoryRecordsGrid,
            AuxiliaryRegisterDetails
        },
        props: {
            id: {
                type: Number,
                required: true
            },
            returnUrl: {
                type: String,
                default() {
                    return '';
                }
            }
        },
        data() {
            return {
                loading: false,
                externalSupplier: new ExternalSupplierDetails(),
                externalSupplierHistoryGridColumns: externalSupplierHistoryGridColumns,
                externalSupplierHistoryGridSchemaModelFields: externalSupplierHistoryGridSchemaModelFields
            };
        },
        methods: {
            backToRegister() {
                this.$router.push(this.returnUrl || '/registers/externalSuppliers');
            },
            loadData() {
                this.loading = true;
                this.$http
                    .get(`/api/externalSupplier/GetDetails/${this.id}`)
                    .then(result => {
                        this.externalSupplier = new ExternalSupplierDetails(result.data);
                        if (this.externalSupplier.company) {
                            this.externalSupplier.company.contact = null;
                        }
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('buttons.load.btnText'),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        })
                    }).then(() => { this.loading = false; });
            }
        },
        mounted() {
            this.loadData();
        },
    };
</script>

<style scoped>
    input[readonly] {
        background-color: whitesmoke;
    }

    textarea[readonly] {
        background-color: whitesmoke;
    }
</style>
